import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Maximize2, Minimize2, Layers, Building2, Store } from "lucide-react";
import api from "../lib/api";
import MapView, { MapLegend } from "../components/MapView";
import { Section } from "../components/UI";

const FILTER = [
  { key: "semua", label: "Semua", icon: Layers },
  { key: "sppg", label: "SPPG", icon: Building2 },
  { key: "supplier", label: "Supplier", icon: Store },
];

/** Pilihan tampilan titik peta: Semua / hanya SPPG / hanya Supplier. */
function FilterTitik({ nilai, onUbah, jumlah }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="group" data-testid="peta-filter">
      {FILTER.map((f) => {
        const aktif = nilai === f.key;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => onUbah(f.key)}
            aria-pressed={aktif}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              aktif ? "bg-white text-[#045498] shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`peta-filter-${f.key}`}
          >
            <f.icon className="w-3.5 h-3.5" />
            {f.label}
            <span className={`rounded-full px-1.5 text-[10px] ${aktif ? "bg-blue-50 text-[#045498]" : "bg-slate-200 text-slate-600"}`}>
              {jumlah[f.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function PetaPage() {
  const [points, setPoints] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [tampil, setTampil] = useState("semua");

  // "Semua" juga memuat titik pengiriman aktif; filter SPPG/Supplier hanya
  // menampilkan lokasi jenis tersebut.
  const titikTampil = useMemo(
    () => (tampil === "semua" ? points : points.filter((p) => p.type === tampil)),
    [points, tampil]
  );
  const jumlah = useMemo(() => ({
    semua: points.length,
    sppg: points.filter((p) => p.type === "sppg").length,
    supplier: points.filter((p) => p.type === "supplier").length,
  }), [points]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/map/points");
      setPoints(data.points || []);
    })();
  }, []);

  // ESC key to exit fullscreen
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setFullscreen(false);
  }, []);

  useEffect(() => {
    if (fullscreen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [fullscreen, handleKeyDown]);

  const toggleFullscreen = () => setFullscreen((f) => !f);

  return (
    <>
      {/* Normal view */}
      {!fullscreen && (
        <div className="space-y-6" data-testid="peta-page">
          <header className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="kedan-label text-slate-500">Pemetaan</div>
              <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Peta Distribusi</h1>
              <p className="text-slate-500 text-sm mt-1">
                Visualisasi lokasi SPPG, supplier, dan titik pengiriman aktif di seluruh Sumatera Utara.
              </p>
            </div>
            <button onClick={toggleFullscreen} className="kedan-btn-secondary" data-testid="fullscreen-btn">
              <Maximize2 className="w-4 h-4" /> Fullscreen
            </button>
          </header>
          <Section title="Sebaran" subtitle={`${titikTampil.length} titik terpantau`} action={<MapLegend tampil={tampil} />}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <FilterTitik nilai={tampil} onUbah={setTampil} jumlah={jumlah} />
            </div>
            <MapView points={titikTampil} height={620} />
          </Section>
        </div>
      )}

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-white flex flex-col"
          data-testid="peta-fullscreen"
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-display font-bold text-slate-900">Peta Distribusi</h2>
              <span className="text-xs text-slate-500">{titikTampil.length} titik terpantau</span>
              <FilterTitik nilai={tampil} onUbah={setTampil} jumlah={jumlah} />
              <MapLegend className="hidden lg:flex ml-1" tampil={tampil} />
            </div>
            <button onClick={toggleFullscreen} className="kedan-btn-secondary" data-testid="exit-fullscreen-btn">
              <Minimize2 className="w-4 h-4" /> Keluar Fullscreen
            </button>
          </div>
          {/* Map fills remaining space */}
          <div className="flex-1">
            <MapView points={titikTampil} height="100%" />
          </div>
        </div>
      )}
    </>
  );
}
